<template>
  <section>
    <v-container fluid grid-list-md>
      <v-row>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="primaryClientId"
            :items="clients"
            item-text="name"
            item-value="id"
            label="Primary client"
            outlined
            dense
            :loading="loadingClients"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="secondaryClientId"
            :items="availableSecondaryClients"
            item-text="name"
            item-value="id"
            label="Child client"
            outlined
            dense
            :disabled="!primaryClientId"
          ></v-autocomplete>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-btn color="primary" :disabled="!secondaryClientId" :loading="saving" @click="addAccess">
            <v-icon left>fas fa-link</v-icon>
            Link client
          </v-btn>
        </v-col>
      </v-row>

      <v-data-table
        class="mt-6"
        :headers="headers"
        :items="linkedClientRows"
        :loading="loadingAccess"
        item-key="accessKey"
        disable-pagination
        hide-default-footer
      >
        <template v-slot:item.primary_client="{ item }">
          <span>{{ clientName(item.primary_client) }}</span>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon small class="ml-1" v-on="on" @click="copyClientName(item.primary_client)">
                <v-icon small>fas fa-copy</v-icon>
              </v-btn>
            </template>
            <span>Copy client name</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon small v-on="on" @click="openClient(item.primary_client)">
                <v-icon small>fas fa-external-link-alt</v-icon>
              </v-btn>
            </template>
            <span>Open client</span>
          </v-tooltip>
        </template>
        <template v-slot:item.secondary_client="{ item }">
          <span>{{ clientName(item.secondary_client) }}</span>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon small class="ml-1" v-on="on" @click="copyClientName(item.secondary_client)">
                <v-icon small>fas fa-copy</v-icon>
              </v-btn>
            </template>
            <span>Copy client name</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon small v-on="on" @click="openClient(item.secondary_client)">
                <v-icon small>fas fa-external-link-alt</v-icon>
              </v-btn>
            </template>
            <span>Open client</span>
          </v-tooltip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon color="error" :disabled="saving" @click="requestRemoveAccess(item)">
            <v-icon>fas fa-unlink</v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <v-dialog v-model="unlinkDialog" max-width="480" persistent>
        <v-card>
          <v-card-title class="headline">Unlink client access?</v-card-title>
          <v-card-text v-if="accessToRemove">
            This will remove access from {{ clientName(accessToRemove.primary_client) }} to {{ clientName(accessToRemove.secondary_client) }}.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text :disabled="saving" @click="cancelRemoveAccess">Cancel</v-btn>
            <v-btn color="error" :loading="saving" @click="confirmRemoveAccess">Unlink</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </section>
</template>

<script>
  import axios from 'axios'

  export default {
    name: 'ClientAccess',
    data() {
      return {
        clients: [],
        primaryClientId: null,
        secondaryClientId: null,
        linkedClients: [],
        loadingClients: false,
        loadingAccess: false,
        saving: false,
        unlinkDialog: false,
        accessToRemove: null,
        headers: [
          { text: 'Primary client', value: 'primary_client' },
          { text: 'Child client', value: 'secondary_client' },
          { text: 'Actions', value: 'actions', sortable: false, align: 'end' }
        ]
      }
    },
    computed: {
      linkedClientRows() {
        return this.linkedClients
          .filter((link) => link.primary_client !== undefined && link.secondary_client !== undefined)
          .map((link) => Object.assign({}, link, {
            accessKey: this.clientId(link.primary_client) + '-' + this.clientId(link.secondary_client)
          }))
      },
      availableSecondaryClients() {
        const linkedClientIds = this.linkedClientRows
          .filter((link) => this.clientId(link.primary_client) === this.primaryClientId)
          .map((link) => this.clientId(link.secondary_client))
        return this.clients.filter((client) => client.id !== this.primaryClientId && !linkedClientIds.includes(client.id))
      }
    },
    methods: {
      async getClients() {
        this.loadingClients = true
        try {
          const response = await axios.get(process.env.API_URL + '/clients?inactive=true')
          this.clients = Object.values(response.data).sort((firstClient, secondClient) => firstClient.name.localeCompare(secondClient.name))
        } catch (error) {
          this.displayError(error)
        } finally {
          this.loadingClients = false
        }
      },
      async getLinkedClients() {
        this.loadingAccess = true
        try {
          const response = await axios.get(process.env.API_URL + '/clients/access')
          this.linkedClients = Object.values(response.data)
        } catch (error) {
          this.displayError(error)
        } finally {
          this.loadingAccess = false
        }
      },
      async addAccess() {
        this.saving = true
        try {
          await axios.post(process.env.API_URL + '/clients/access', {
            primary_client: this.primaryClientId,
            secondary_client: this.secondaryClientId
          })
          this.secondaryClientId = null
          await this.getLinkedClients()
        } catch (error) {
          this.displayError(error)
        } finally {
          this.saving = false
        }
      },
      requestRemoveAccess(link) {
        this.accessToRemove = link
        this.unlinkDialog = true
      },
      cancelRemoveAccess() {
        this.unlinkDialog = false
        this.accessToRemove = null
      },
      async confirmRemoveAccess() {
        if (!this.accessToRemove) return
        const link = this.accessToRemove
        this.unlinkDialog = false
        this.accessToRemove = null
        await this.removeAccess(link)
      },
      async removeAccess(link) {
        this.saving = true
        try {
          await axios.delete(process.env.API_URL + '/clients/access', {
            data: {
              primary_client: this.clientId(link.primary_client),
              secondary_client: this.clientId(link.secondary_client)
            }
          })
          await this.getLinkedClients()
        } catch (error) {
          this.displayError(error)
        } finally {
          this.saving = false
        }
      },
      clientId(client) {
        return typeof client === 'object' ? client.id : client
      },
      clientName(client) {
        if (typeof client === 'object') return client.name
        const matchingClient = this.clients.find((item) => item.id === client)
        return matchingClient ? matchingClient.name : client
      },
      async copyClientName(client) {
        const name = this.clientName(client)
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(name)
        } else {
          const textArea = document.createElement('textarea')
          textArea.value = name
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        }
        this.$store.commit('displaySnackbar', 'Client name copied.')
      },
      openClient(client) {
        const route = this.$router.resolve({ path: '/edit-client', query: { id: this.clientId(client) } })
        const clientWindow = window.open(route.href, '_blank')
        if (clientWindow) clientWindow.opener = null
      },
      displayError(error) {
        const message = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : 'Unable to update client access.'
        this.$store.commit('displaySnackbar', 'ERROR ' + message)
      }
    },
    mounted() {
      this.getClients()
      this.getLinkedClients()
    }
  }
</script>


// WEBPACK FOOTER //
// src/components/pages/Admin/ClientAccess.vue